/**
 * Tag Service
 * 
 * Service for managing transaction tags using Supabase client
 * Following the service pattern from accounts feature
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import { 
  Tag, 
  CreateTagInput, 
  UpdateTagInput,
  TagListParams,
  TagListResponse 
} from '../types/tag.types';

export interface TagServiceResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

// Helper function to convert database row to Tag
function convertDbRowToTag(row: any): Tag {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    color: row.color,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

// Helper function to convert Tag to database insert format
function convertTagToDbInsert(input: CreateTagInput, userId: string): any {
  return {
    user_id: userId,
    name: input.name,
    color: input.color
  };
}

class TagService {
  /**
   * List all tags for the current user
   */
  async listTags(params?: TagListParams): Promise<TagServiceResponse<TagListResponse>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      let query = supabase
        .from('transaction_tags')
        .select('*', { count: 'exact' })
        .eq('user_id', userId);

      // Apply search filter
      if (params?.search) {
        query = query.ilike('name', `%${params.search}%`);
      }

      // Order by name by default
      query = query.order('name', { ascending: true });

      const { data, error, count } = await query;

      if (error) {
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch tags: ${error.message}`,
            details: error
          }
        };
      }

      const tags = (data || []).map(convertDbRowToTag);

      return {
        data: {
          tags,
          total: count || 0
        }
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Get a tag by ID
   */
  async getTagById(id: string): Promise<TagServiceResponse<Tag>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      const { data, error } = await supabase
        .from('transaction_tags')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Tag not found',
              details: error
            }
          };
        }
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch tag: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: convertDbRowToTag(data)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Create a new tag
   */
  async createTag(input: CreateTagInput): Promise<TagServiceResponse<Tag>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Validate input
      if (!input.name || input.name.trim().length === 0) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Tag name is required',
            details: 'Name cannot be empty'
          }
        };
      }

      if (input.name.length > 50) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Tag name must be 50 characters or less',
            details: `Name length: ${input.name.length}`
          }
        };
      }

      if (!input.color || !/^#[0-9A-Fa-f]{6}$/.test(input.color)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Tag color must be a valid hex color code (e.g., #FF5733)',
            details: `Invalid color: ${input.color}`
          }
        };
      }

      // Check for duplicate name (case-insensitive)
      const existing = await supabase
        .from('transaction_tags')
        .select('id')
        .eq('user_id', userId)
        .ilike('name', input.name)
        .single();

      if (existing.data) {
        return {
          error: {
            code: 'DUPLICATE_ERROR',
            message: 'Tag name already exists',
            details: 'A tag with this name already exists for your account'
          }
        };
      }

      const insertData = convertTagToDbInsert(input, userId);

      const { data, error } = await supabase
        .from('transaction_tags')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'INSERT_ERROR',
            message: `Failed to create tag: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: convertDbRowToTag(data)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Update an existing tag
   */
  async updateTag(id: string, input: UpdateTagInput): Promise<TagServiceResponse<Tag>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Check if tag exists and belongs to user
      const existing = await this.getTagById(id);
      if (existing.error || !existing.data) {
        return existing;
      }

      // Validate name if provided
      if (input.name !== undefined) {
        if (input.name.trim().length === 0) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Tag name cannot be empty',
              details: 'Name is required'
            }
          };
        }

        if (input.name.length > 50) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Tag name must be 50 characters or less',
              details: `Name length: ${input.name.length}`
            }
          };
        }

        // Check for duplicate name (excluding current tag)
        const duplicate = await supabase
          .from('transaction_tags')
          .select('id')
          .eq('user_id', userId)
          .ilike('name', input.name)
          .neq('id', id)
          .single();

        if (duplicate.data) {
          return {
            error: {
              code: 'DUPLICATE_ERROR',
              message: 'Tag name already exists',
              details: 'Another tag with this name already exists'
            }
          };
        }
      }

      // Validate color if provided
      if (input.color !== undefined && !/^#[0-9A-Fa-f]{6}$/.test(input.color)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Tag color must be a valid hex color code (e.g., #FF5733)',
            details: `Invalid color: ${input.color}`
          }
        };
      }

      // Build update object
      const updateData: any = {};
      if (input.name !== undefined) updateData.name = input.name;
      if (input.color !== undefined) updateData.color = input.color;

      const { data, error } = await supabase
        .from('transaction_tags')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'UPDATE_ERROR',
            message: `Failed to update tag: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: convertDbRowToTag(data)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Delete a tag
   */
  async deleteTag(id: string): Promise<TagServiceResponse<void>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Check if tag exists and belongs to user
      const existing = await this.getTagById(id);
      if (existing.error || !existing.data) {
        return {
          error: existing.error,
          data: undefined
        };
      }

      // TODO: Check if tag has transactions (will be implemented when transactions feature is added)
      // For now, we'll allow deletion

      const { error } = await supabase
        .from('transaction_tags')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) {
        return {
          error: {
            code: 'DELETE_ERROR',
            message: `Failed to delete tag: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: undefined
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Bulk delete tags
   */
  async bulkDeleteTags(ids: string[]): Promise<TagServiceResponse<void>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Verify all tags belong to user
      for (const id of ids) {
        const tag = await this.getTagById(id);
        if (tag.error || !tag.data) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: `Tag ${id} not found or not accessible`,
              details: tag.error
            }
          };
        }
      }

      const { error } = await supabase
        .from('transaction_tags')
        .delete()
        .in('id', ids)
        .eq('user_id', userId);

      if (error) {
        return {
          error: {
            code: 'DELETE_ERROR',
            message: `Failed to delete tags: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: undefined
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }

  /**
   * Bulk update tags
   */
  async bulkUpdateTags(ids: string[], updates: Partial<Tag>): Promise<TagServiceResponse<Tag[]>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated. Please sign in.',
            details: 'No authenticated user found'
          }
        };
      }

      // Build update object (only allow color for bulk update)
      const updateData: any = {};
      if (updates.color !== undefined) {
        if (!/^#[0-9A-Fa-f]{6}$/.test(updates.color)) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Tag color must be a valid hex color code (e.g., #FF5733)',
              details: `Invalid color: ${updates.color}`
            }
          };
        }
        updateData.color = updates.color;
      }

      const { data, error } = await supabase
        .from('transaction_tags')
        .update(updateData)
        .in('id', ids)
        .eq('user_id', userId)
        .select();

      if (error) {
        return {
          error: {
            code: 'UPDATE_ERROR',
            message: `Failed to update tags: ${error.message}`,
            details: error
          }
        };
      }

      return {
        data: (data || []).map(convertDbRowToTag)
      };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          details: error
        }
      };
    }
  }
}

export const tagService = new TagService();

